import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { keyed } from 'lit/directives/keyed.js';
import { acceptPop, parse, tendPath, type Route } from './router';
import './views/home-view';
import './views/record-view';
import './views/moment-view';
import './views/practice-view';
import './views/review-view';
import './views/exam-view';
import './views/onboarding-view';
import './views/signin-view';
import './views/join-view';
import './views/recover-view';
import './views/circle-view';
import './views/activity-view';
import './views/friend-link-view';
import './views/feedback-card';
import './views/opening-view';
import { releaseFirstNight } from './first-night';

// PROTOTYPE — dev-only throwaway route; the dead branch drops out of production builds.
if (import.meta.env.DEV) void import('./views/prototype-social/social-prototype');
if (import.meta.env.DEV) void import('./views/prototype-town/town-prototype');
if (import.meta.env.DEV) void import('./views/prototype-tech/tech-prototype');
if (import.meta.env.DEV) void import('./views/prototype-round/round-prototype');
if (import.meta.env.DEV) void import('./views/prototype-battle/battle-prototype');
if (import.meta.env.DEV) void import('./views/prototype-raid/raid-preview');
if (import.meta.env.DEV) void import('./views/prototype-brand/brand-prototype');
if (import.meta.env.DEV) void import('./views/prototype-arrival/arrival-prototype');
if (import.meta.env.DEV) void import('./views/prototype-first-night/first-night-prototype');
if (import.meta.env.DEV) void import('./views/prototype-sky/sky-prototype');
// The town phone check (v1 ticket 01): its own chunk, with the town code, loaded only on /town-check.
const loadTownCheck = () => import('./views/town-check-view');
// Raids (v1 ticket 47): the armoury and a raid's screens, their own chunk.
const loadRaid = () => import('./views/raid/raid-screens');
// A moment landing in the town, and its first lesson there (v1 ticket 41): its own chunk, with the town code.
const loadLand = () => import('./views/land-view');
// MOTHBALLED live voice tutor (kept for the voice exam): dev-only, so @google/genai stays out of production builds.
if (import.meta.env.DEV) void import('./views/tutor-view');

@customElement('li-app')
export class LiApp extends LitElement {
  @state() private route: Route = parse(location.pathname, location.search);

  protected createRenderRoot() { return this; }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('popstate', this.onPop);
    window.addEventListener('li-language', this.onLanguage);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('popstate', this.onPop);
    window.removeEventListener('li-language', this.onLanguage);
  }
  private onPop = () => {
    if (!acceptPop()) return;
    this.route = parse(location.pathname, location.search);
    // The first night's held layers wait for home's land; any other page takes them away (v1 ticket 40).
    if (this.route.name !== 'home' && this.route.name !== 'moment' && this.route.name !== 'opening' && this.route.name !== 'land') void releaseFirstNight();
  };
  /**
   * The interface language settled (or changed on the join form): re-render
   * everything on screen in place, so labels swap without losing form state.
   */
  private onLanguage = () => {
    // Exercise cards render inside the surface's shadow root: walk into it.
    const walk = (root: Document | ShadowRoot) => {
      for (const el of Array.from(root.querySelectorAll('*'))) {
        (el as Element & { requestUpdate?: () => void }).requestUpdate?.();
        if (el.shadowRoot) walk(el.shadowRoot);
      }
    };
    walk(document);
  };

  render() {
    const r = this.route;
    switch (r.name) {
      case 'record': return html`<record-view></record-view>`;
      // A reminder's tap (`?reminder=`) on a page already on the round starts a fresh queue: the nonce is part of the key.
      case 'review': return keyed(new URLSearchParams(location.search).get('reminder') ?? '', html`<review-view></review-view>`);
      case 'exam': return html`<exam-view></exam-view>`;
      case 'start': return html`<onboarding-view></onboarding-view>`;
      case 'activity': return html`<activity-view></activity-view>`;
      case 'circle': return html`<circle-view .code=${r.code ?? ''} .returnTo=${r.returnTo ?? ''}></circle-view>`;
      case 'friend': return keyed(r.token, html`<friend-link-view .token=${r.token}></friend-link-view>`);
      case 'raid-new': void loadRaid(); return keyed(`${r.friend}/${r.edit ?? ''}`, html`<raid-new-view .friend=${r.friend} .edit=${r.edit ?? ''}></raid-new-view>`);
      case 'raid-spoils': void loadRaid(); return html`<raid-spoils-view .returnTo=${r.returnTo ?? ''}></raid-spoils-view>`;
      case 'raid-tour': void loadRaid(); return keyed(`${r.next ?? ''}:${r.context ?? ''}`, html`<raid-tour .next=${r.next} .context=${r.context}></raid-tour>`);
      case 'raid': void loadRaid(); return keyed(r.id, html`<raid-view .raidId=${r.id} .returnTo=${r.returnTo ?? ''}></raid-view>`);
      case 'signin': return html`<signin-view></signin-view>`;
      case 'town-check': void loadTownCheck(); return html`<town-check-view></town-check-view>`;
      // The first night's opening (v1 ticket 40): the view is small; the cinematic is the `opening` chunk it loads.
      case 'opening': return html`<opening-view></opening-view>`;
      case 'join': return html`<join-view .token=${r.token}></join-view>`;
      case 'recover': return html`<recover-view .token=${r.token}></recover-view>`;
      // A moment is a panel over home's town: one template for both, so the town stays drawn as it opens and closes.
      case 'moment': return homeWith(r.id);
      case 'practice': return html`<practice-view .momentId=${r.id} .returnTo=${r.returnTo}></practice-view>`;
      // A new round per target: the view loads its queue once, when it connects.
      case 'tend': return keyed(tendPath(r.tend), html`<review-view .tend=${r.tend}></review-view>`);
      // The view reads `#lesson` (straight to the first lesson) as it connects, so the hash is part of its key.
      case 'land': void loadLand(); return keyed(`${r.id}${location.hash === '#lesson' ? '#lesson' : ''}`, html`<land-view .momentId=${r.id}></land-view>`);
      case 'tutor': return import.meta.env.DEV ? html`<tutor-view .momentId=${r.id}></tutor-view>` : nothing;
      case 'prototype-social': return import.meta.env.DEV ? html`<social-prototype></social-prototype>` : nothing;
      case 'prototype-town': return import.meta.env.DEV ? html`<town-prototype></town-prototype>` : nothing;
      case 'prototype-tech': return import.meta.env.DEV ? html`<tech-prototype></tech-prototype>` : nothing;
      case 'prototype-round': return import.meta.env.DEV ? html`<round-prototype></round-prototype>` : nothing;
      case 'prototype-battle': return import.meta.env.DEV ? html`<battle-prototype></battle-prototype>` : nothing;
      case 'prototype-raid': return import.meta.env.DEV ? html`<raid-preview></raid-preview>` : nothing;
      case 'prototype-brand': return import.meta.env.DEV ? html`<brand-prototype></brand-prototype>` : nothing;
      case 'prototype-arrival': return import.meta.env.DEV ? html`<arrival-prototype></arrival-prototype>` : nothing;
      case 'prototype-first-night': return import.meta.env.DEV ? html`<first-night-prototype></first-night-prototype>` : nothing;
      case 'prototype-sky': return import.meta.env.DEV ? html`<sky-prototype></sky-prototype>` : nothing;
      default: return homeWith('');
    }
  }
}

/** Home, with a moment's panel up over its town when `momentId` is set (`/moment/<id>`). */
const homeWith = (momentId: string) => html`<home-view .momentId=${momentId}></home-view>`;
